import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQueryClient } from '@tanstack/vue-query';
import { addToast } from '@teamworksdev/vuetify';
import { useGmApi } from '@/composables/useGmApi';
import { useTeams } from '@/composables/useTeams';
import { useMainStore } from '@/stores/mainStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { makeMeQueryKey } from '@/utils/queries';

type QueryValue = string | string[] | null | undefined;

export interface DeepLinkParams {
  token?: string;
  target?: string;
  athleteTwrn?: string;
  axleTwrn?: string;
  teamTwrn?: string;
}

function coerceParam(value: QueryValue): string | undefined {
  if (Array.isArray(value)) {
    const first = value[0];
    return first && first.length > 0 ? first : undefined;
  }
  if (!value || value.length === 0) {
    return undefined;
  }
  return value;
}

export function parseDeepLinkParams(
  query: Record<string, QueryValue>,
): DeepLinkParams {
  const result: DeepLinkParams = {};

  const token = coerceParam(query.token);
  if (token !== undefined) result.token = token;

  const target = coerceParam(query.target);
  if (target !== undefined) result.target = target;

  const athleteTwrn = coerceParam(query.athleteTwrn);
  if (athleteTwrn !== undefined) result.athleteTwrn = athleteTwrn;

  const axleTwrn = coerceParam(query.axleTwrn);
  if (axleTwrn !== undefined) result.axleTwrn = axleTwrn;

  const teamTwrn = coerceParam(query.teamTwrn);
  if (teamTwrn !== undefined) result.teamTwrn = teamTwrn;

  return result;
}

export function useDeepLink() {
  const route = useRoute();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const { getMe, swapAxleTwrnForGmTwrn } = useGmApi();
  const { hasTeamAccess } = useTeams();
  const mainStore = useMainStore();
  const filtersStore = useFiltersStore();

  const { token, target, athleteTwrn, axleTwrn, teamTwrn } = parseDeepLinkParams(
    route.query as Record<string, QueryValue>,
  );
  const hasDeepLinkIntent = !!(target || athleteTwrn || axleTwrn || teamTwrn);

  async function resolveDeepLink(): Promise<{
    name: string;
    query?: Record<string, string>;
  }> {
    await queryClient.ensureQueryData({
      queryKey: makeMeQueryKey(),
      queryFn: getMe,
    });

    let effectiveAthleteTwrn = athleteTwrn;
    if (!effectiveAthleteTwrn && axleTwrn) {
      try {
        const result = await swapAxleTwrnForGmTwrn(axleTwrn);
        effectiveAthleteTwrn = result.twrn;
      } catch {
        console.warn('[resolveDeepLink] unresolvable axleTwrn → proceed unfiltered, no error shown');
      }
    }

    let teamSwitched = false;

    if (teamTwrn) {
      if (hasTeamAccess(teamTwrn)) {
        mainStore.setTeamTwrn(teamTwrn);
        teamSwitched = true;
      } else {
        addToast({
          type: 'warning',
          title: t('deepLink.teamAccessDenied.title'),
          description: t('deepLink.teamAccessDenied.description'),
        });
      }
    } else if (effectiveAthleteTwrn) {
      const inferred = await inferTeamFromAthlete(effectiveAthleteTwrn);
      if (inferred && hasTeamAccess(inferred)) {
        mainStore.setTeamTwrn(inferred);
        teamSwitched = true;
      }
    }

    if (teamSwitched) {
      await queryClient.invalidateQueries();
    }

    if (target === 'payments' && effectiveAthleteTwrn) {
      const prevFilters = filtersStore.allPaymentFilters;
      if (prevFilters) {
        filtersStore.setAllPaymentFilters({
          ...prevFilters,
          athletes: [effectiveAthleteTwrn],
        });
      }
      // if prevFilters is null: URL fallback (?paymentAthletes) handles it via storage-first priority
    }
    // agreements: no storage write needed — URL-first, ?athletes in destination is sufficient

    const destination = buildDestination(target, effectiveAthleteTwrn);
    const isValidTarget = target === 'agreements' || target === 'payments';
    if (target !== undefined && !isValidTarget) {
      addToast({
        type: 'warning',
        title: t('deepLink.invalidTarget.title'),
        description: t('deepLink.invalidTarget.description'),
      });
    }
    return destination;
  }

  return {
    token,
    hasDeepLinkIntent,
    resolveDeepLink,
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function inferTeamFromAthlete(_athleteTwrn: string): Promise<string | null> {
  return null;
}

export function buildDestination(
  target: string | undefined,
  athleteTwrn: string | undefined,
): {
  name: string;
  query?: Record<string, string>;
} {
  if (target === 'agreements') {
    return {
      name: 'Agreements',
      query: athleteTwrn ? { athletes: athleteTwrn } : {},
    };
  }
  if (target === 'payments') {
    return {
      name: 'TeamPayments',
      query: athleteTwrn ? { paymentAthletes: athleteTwrn } : {},
    };
  }
  return { name: 'TeamBudgets' };
}
